import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

import scitelyLogo from '../../images/logos/scitely.png';
import projectinboxLogo from '../../images/logos/projectinbox.png';
import powerfoxmediaLogo from '../../images/logos/powerfoxmedia.png';
import nineprosLogo from '../../images/logos/9pros.png';
import inst8Logo from '../../images/logos/inst8.png';
import gocheapwebsiteLogo from '../../images/logos/gocheapwebsite.png';
import remotasksLogo from '../../images/logos/remotasks.png';
import gmxLogo from '../../images/logos/gmx.png';
import onlinejobsphLogo from '../../images/logos/onlinejobsph.png';
import theconceptgroupLogo from '../../images/logos/theconceptgroup.png';

const StyledJobsSection = styled.section`
  max-width: 700px;

  .inner {
    display: flex;

    @media (max-width: 600px) {
      display: block;
    }

    // Prevent container from jumping
    @media (min-width: 700px) {
      min-height: 340px;
    }
  }
`;

const StyledLogoCarousel = styled.div`
  margin-top: 30px;
  margin-bottom: -100px;
  padding: 20px 0 0 0;
  overflow: hidden;
  position: relative;

  .carousel-title {
    text-align: center;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin-bottom: 30px;
  }

  .carousel-wrapper {
    position: relative;
    width: 100%;
    height: 80px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .carousel-track {
    position: relative;
    width: 800px;
    height: 80px;

    @media (max-width: 600px) {
      width: 500px;
      height: 60px;
    }
  }

  .logo-item {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 160px;
    height: 80px;
    background: transparent;
    transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                filter 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    @media (max-width: 600px) {
      width: 100px;
      height: 60px;
    }

    img {
      width: 120px;
      height: 60px;
      object-fit: contain;
      background: transparent;

      @media (max-width: 600px) {
        width: 80px;
        height: 40px;
      }
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  @media (max-width: 600px) {
    display: flex;
    overflow-x: auto;
    width: calc(100% + 100px);
    padding-left: 50px;
    margin-left: -50px;
    margin-bottom: 30px;
  }
  @media (max-width: 480px) {
    width: calc(100% + 50px);
    padding-left: 25px;
    margin-left: -25px;
  }

  li {
    &:first-of-type {
      @media (max-width: 600px) {
        margin-left: 50px;
      }
      @media (max-width: 480px) {
        margin-left: 25px;
      }
    }
    &:last-of-type {
      @media (max-width: 600px) {
        padding-right: 50px;
      }
      @media (max-width: 480px) {
        padding-right: 25px;
      }
    }
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 20px 2px;
  border-left: 2px solid var(--lightest-navy);
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--slate)')};
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  text-align: left;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 0 15px 2px;
  }
  @media (max-width: 600px) {
    ${({ theme }) => theme.mixins.flexCenter};
    min-width: 120px;
    max-width: 150px;
    padding: 0 15px;
    border-left: 0;
    border-bottom: 2px solid var(--lightest-navy);
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
  }

  &:hover,
  &:focus {
    background-color: var(--light-navy);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: var(--green);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;

  @media (max-width: 600px) {
    top: auto;
    bottom: 0;
    width: 100%;
    max-width: var(--tab-width);
    height: 2px;
    margin-left: 50px;
    transform: translateX(calc(${({ activeTabId }) => activeTabId} * var(--tab-width)));
  }
  @media (max-width: 480px) {
    margin-left: 25px;
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  margin-left: 20px;

  @media (max-width: 600px) {
    margin-left: 0;
  }
`;

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 5px;

  @media (max-width: 600px) {
    padding: 10px 0;
  }

  ul {
    ${({ theme }) => theme.mixins.fancyList};

    @media (max-width: 600px) {
      padding-left: 20px;

      li {
        margin-bottom: 15px;
        padding-left: 25px;
        font-size: var(--fz-md);
        line-height: 1.5;

        &:before {
          left: 0;
        }
      }
    }

    @media (max-width: 480px) {
      padding-left: 15px;

      li {
        margin-bottom: 12px;
        padding-left: 20px;
        font-size: var(--fz-sm);
      }
    }
  }

  h3 {
    margin-bottom: 2px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;

    @media (max-width: 600px) {
      font-size: var(--fz-xl);
    }

    @media (max-width: 480px) {
      font-size: var(--fz-lg);
      display: flex;
      flex-direction: column;

      .company {
        margin-top: 5px;
      }
    }

    .company {
      color: var(--green);
    }
  }

  .range {
    margin-bottom: 25px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);

    @media (max-width: 600px) {
      margin-bottom: 20px;
    }
  }
`;

const partnerLogos = [
  { name: 'The Concept Group', logo: theconceptgroupLogo, scale: 1.4 },
  { name: 'Scitely', logo: scitelyLogo, scale: 1.4 },
  { name: 'ProjectInbox', logo: projectinboxLogo, scale: 1.4 },
  { name: '9Pros', logo: nineprosLogo, scale: 1 },
  { name: 'PowerFoxMedia', logo: powerfoxmediaLogo, scale: 1.4 },
  { name: 'Inst8', logo: inst8Logo, scale: 0.9 },
  { name: 'GoCheapWebsite', logo: gocheapwebsiteLogo, scale: 1.4 },
  { name: 'Remotasks', logo: remotasksLogo, scale: 1.8 },
  { name: 'GMX', logo: gmxLogo, scale: 1 },
  { name: 'OnlineJobs.PH', logo: onlinejobsphLogo, scale: 1.4 },
];

const Jobs = () => {
  const [activeLogoIndex, setActiveLogoIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLogoIndex(prev => (prev + 1) % partnerLogos.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const getLogoStyle = (index) => {
    const total = partnerLogos.length;
    let diff = index - activeLogoIndex;

    // Normalize to range [-total/2, total/2]
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    const baseOffset = 160; // spacing between logos

    // Position and styles based on distance from center
    if (diff === 0) {
      return {
        transform: `translate(-50%, -50%) translateX(0px) scale(1.1)`,
        opacity: 1,
        filter: 'grayscale(0%) brightness(1)',
        zIndex: 5,
      };
    }
    if (diff === 1) {
      return {
        transform: `translate(-50%, -50%) translateX(${baseOffset}px) scale(0.85)`,
        opacity: 0.6,
        filter: 'grayscale(100%) brightness(0.7) blur(1px)',
        zIndex: 4,
      };
    }
    if (diff === -1) {
      return {
        transform: `translate(-50%, -50%) translateX(${-baseOffset}px) scale(0.85)`,
        opacity: 0.6,
        filter: 'grayscale(100%) brightness(0.7) blur(1px)',
        zIndex: 4,
      };
    }
    if (diff === 2) {
      return {
        transform: `translate(-50%, -50%) translateX(${baseOffset * 2}px) scale(0.7)`,
        opacity: 0.3,
        filter: 'grayscale(100%) brightness(0.5) blur(2px)',
        zIndex: 3,
      };
    }
    if (diff === -2) {
      return {
        transform: `translate(-50%, -50%) translateX(${-baseOffset * 2}px) scale(0.7)`,
        opacity: 0.3,
        filter: 'grayscale(100%) brightness(0.5) blur(2px)',
        zIndex: 3,
      };
    }
    // Hidden logos
    return {
      transform: `translate(-50%, -50%) translateX(${diff > 0 ? baseOffset * 3 : -baseOffset * 3}px) scale(0.5)`,
      opacity: 0,
      filter: 'grayscale(100%) blur(4px)',
      zIndex: 1,
    };
  };

  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const focusTab = () => {
    if (tabs.current[tabFocus]) {
      tabs.current[tabFocus].focus();
      return;
    }
    // If we're at the end, go to the start
    if (tabFocus >= tabs.current.length) {
      setTabFocus(0);
    }
    // If we're at the start, move to the end
    if (tabFocus < 0) {
      setTabFocus(tabs.current.length - 1);
    }
  };

  // Only re-run the effect if tabFocus changes
  useEffect(() => focusTab(), [tabFocus]);

  // Focus on tabs when using up & down arrow keys
  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I’ve Worked</h2>

      <div className="inner">
        <StyledTabList role="tablist" aria-label="Job tabs" onKeyDown={e => onKeyDown(e)}>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { company } = node.frontmatter;
              return (
                <StyledTabButton
                  key={i}
                  isActive={activeTabId === i}
                  onClick={() => setActiveTabId(i)}
                  ref={el => (tabs.current[i] = el)}
                  id={`tab-${i}`}
                  role="tab"
                  tabIndex={activeTabId === i ? '0' : '-1'}
                  aria-selected={activeTabId === i ? true : false}
                  aria-controls={`panel-${i}`}>
                  <span>{company}</span>
                </StyledTabButton>
              );
            })}
          <StyledHighlight activeTabId={activeTabId} />
        </StyledTabList>

        <StyledTabPanels>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <a href={url} className="inline-link">
                          {company}
                        </a>
                      </span>
                    </h3>

                    <p className="range">{range}</p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>

      <StyledLogoCarousel>
        <p className="carousel-title">Companies & Partners</p>
        <div className="carousel-wrapper">
          <div className="carousel-track">
            {partnerLogos.map((partner, index) => {
              const style = getLogoStyle(index);
              return (
                <div
                  key={partner.name}
                  className="logo-item"
                  style={style}>
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    style={{ transform: `scale(${partner.scale || 1})` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </StyledLogoCarousel>
    </StyledJobsSection>
  );
};

export default Jobs;
